import { Button, Card, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { Customer } from "@zhihaoscm/entity";
import { Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { dateFormat } from "@/utils";
import { getCustomerIdentityList } from "@/utils/customer";
import RequiredMark from "@/views/components/RequiredMark";

export interface EnterpriseCustomFormData {
    id?: number;
    customerId?: number | string;
    userId?: number | string;
    systemName?: string;
    systemAbbreviation?: string;
    systemHeaderSlogan?: string;
    aiName?: string;
    serviceTel?: string;
    copyright?: string;
    icpFilings?: string;
    internetFilings?: string;
    accessKey?: string;
    secretKey?: string;
    signName?: string;
    loginMsgTemplateId?: string;
    privateDomainName?: string;
    customJson?: unknown;
    userJson?: unknown;
}

interface EnterpriseCustomCustomerInfo {
    id?: number;
    institutionName?: string;
    realName?: string;
    mobile?: string;
    code?: string;
    identity?: number | string;
    memberLevelText?: string;
    memberActiveTime?: string;
    memberExpiryDate?: string;
}

interface EnterpriseCustomUserInfo {
    id?: number;
    name?: string;
}

interface RemoteOption<T> {
    label: string;
    value: number;
    snapshot: T;
}

/** 复用 Vue 企业定制编辑接口。 */
export async function loadEnterpriseCustomForm(id: number | string): Promise<EnterpriseCustomFormData> {
    const { data } = await request.service.get<EnterpriseCustomFormData>(`/enterprise-custom/${id}`);
    return data;
}

/** 复用 Vue 企业定制新增接口。 */
export async function createEnterpriseCustomForm(data: EnterpriseCustomFormData): Promise<unknown> {
    const response = await request.service.post("/enterprise-custom/create", data);
    return response.data;
}

/** 复用 Vue 企业定制更新接口。 */
export async function updateEnterpriseCustomForm(
    id: number | string,
    data: EnterpriseCustomFormData
): Promise<unknown> {
    const response = await request.service.put(`/enterprise-custom/update/${id}`, data);
    return response.data;
}

/** 选择链云用户沿用旧 Vue 的个人认证过滤条件。 */
export async function loadEnterpriseCustomCustomers(
    keyword: string
): Promise<RemoteOption<EnterpriseCustomCustomerInfo>[]> {
    if (!keyword.trim()) return [];
    const { data } = await request.service.get<EnterpriseCustomCustomerInfo[]>("/admin/customer/selector", {
        params: { param: keyword, personalAuth: 1 }
    });
    return data.map(customer => ({
        value: Number(customer.id),
        label: `${customer.institutionName || customer.realName || "--"} | ${customer.mobile || "--"} | ${customer.code || "--"}`,
        snapshot: customer
    }));
}

/** 关联企业定制用户沿用旧 Vue 的 origin=1 账号选择器。 */
export async function loadEnterpriseCustomUsers(keyword: string): Promise<RemoteOption<EnterpriseCustomUserInfo>[]> {
    if (!keyword.trim()) return [];
    const { data } = await request.service.get<EnterpriseCustomUserInfo[]>("/admin/user/selector", {
        params: { name: keyword, origin: 1 }
    });
    return data.map(user => ({ value: Number(user.id), label: user.name || "--", snapshot: user }));
}

const initialForm: EnterpriseCustomFormData = { aiName: "链小云" };

const formFields: Array<{ key: keyof EnterpriseCustomFormData; label: string; required?: boolean }> = [
    { key: "customerId", label: "选择链云用户", required: true },
    { key: "userId", label: "关联企业定制用户", required: true },
    { key: "systemName", label: "系统名称", required: true },
    { key: "systemAbbreviation", label: "系统简称" },
    { key: "systemHeaderSlogan", label: "系统头部slogan", required: true },
    { key: "aiName", label: "AI助理名称" },
    { key: "serviceTel", label: "服务热线", required: true },
    { key: "copyright", label: "版权信息", required: true },
    { key: "icpFilings", label: "ICP备案信息", required: true },
    { key: "internetFilings", label: "联网备案信息", required: true },
    { key: "accessKey", label: "access_key" },
    { key: "secretKey", label: "secret_key" },
    { key: "signName", label: "短信签名", required: true },
    { key: "loginMsgTemplateId", label: "登录短信模板ID" },
    { key: "privateDomainName", label: "私有域名" }
];

const numericFields = new Set<keyof EnterpriseCustomFormData>(["customerId", "userId"]);

const fieldMaxLength: Partial<Record<keyof EnterpriseCustomFormData, number>> = {
    systemName: 20,
    systemAbbreviation: 10,
    systemHeaderSlogan: 20,
    aiName: 10,
    serviceTel: 20,
    copyright: 50,
    icpFilings: 20,
    internetFilings: 20,
    signName: 12
};

const fieldMap = new Map(formFields.map(field => [field.key, field]));
const basicFieldKeys: Array<keyof EnterpriseCustomFormData> = [
    "systemName",
    "systemAbbreviation",
    "systemHeaderSlogan",
    "aiName",
    "serviceTel",
    "copyright",
    "icpFilings",
    "internetFilings"
];
const configFieldKeys: Array<keyof EnterpriseCustomFormData> = [
    "accessKey",
    "secretKey",
    "signName",
    "loginMsgTemplateId",
    "privateDomainName"
];

// 编辑接口会回显旧 Vue 页面使用的客户快照；无快照时保持新增页面不额外发起请求或改变 payload。
const getCustomerInfo = (value: unknown): EnterpriseCustomCustomerInfo | undefined => {
    if (!value || typeof value !== "object") return undefined;
    return value as EnterpriseCustomCustomerInfo;
};

const displayValue = (value: unknown) => (value ? String(value) : "--");

const formatMobile = (mobile?: string) => mobile?.replace(/(\d{3})(\d{4})(\d{4})/g, "$1 $2 $3") || "--";

/** 企业定制表单最小闭环，保留 Vue 字段、必填校验和新增/编辑路径。 */
export function EnterpriseCustomFormPage() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const editing = Boolean(id);
    const [form, setForm] = useState<EnterpriseCustomFormData>(initialForm);
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [customerOptions, setCustomerOptions] = useState<RemoteOption<EnterpriseCustomCustomerInfo>[]>([]);
    const [userOptions, setUserOptions] = useState<RemoteOption<EnterpriseCustomUserInfo>[]>([]);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [searchingUser, setSearchingUser] = useState(false);
    const customerInfo = getCustomerInfo(form.customJson);

    useEffect(() => {
        if (!id) return;
        void loadEnterpriseCustomForm(id)
            .then(data => {
                setForm({ ...initialForm, ...data });
                const customer = getCustomerInfo(data.customJson);
                if (customer?.id) {
                    setCustomerOptions([
                        {
                            value: customer.id,
                            label: `${customer.institutionName || customer.realName || "--"} | ${customer.mobile || "--"} | ${customer.code || "--"}`,
                            snapshot: customer
                        }
                    ]);
                }
                const user = data.userJson as EnterpriseCustomUserInfo | undefined;
                if (user?.id) setUserOptions([{ value: user.id, label: user.name || "--", snapshot: user }]);
            })
            .catch(() => setErrorMessage("企业定制详情加载失败"));
    }, [id]);

    const updateField = (key: keyof EnterpriseCustomFormData, rawValue: string) => {
        const value = numericFields.has(key) ? (rawValue ? Number(rawValue) : "") : rawValue;
        setForm(current => ({ ...current, [key]: value }));
        setErrorMessage("");
    };

    const searchCustomers = async (keyword: string) => {
        setSearchingCustomer(true);
        try {
            setCustomerOptions(await loadEnterpriseCustomCustomers(keyword));
        } finally {
            setSearchingCustomer(false);
        }
    };

    const searchUsers = async (keyword: string) => {
        setSearchingUser(true);
        try {
            setUserOptions(await loadEnterpriseCustomUsers(keyword));
        } finally {
            setSearchingUser(false);
        }
    };

    const validate = () => {
        const field = formFields.find(item => item.required && !String(form[item.key] ?? "").trim());
        if (!field) return true;
        setErrorMessage(`请输入${field.label}`);
        if (field.key === "customerId") setErrorMessage("请选择链云用户");
        if (field.key === "userId") setErrorMessage("请选择关联企业定制用户");
        return false;
    };

    const submit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            if (editing) await updateEnterpriseCustomForm(id, { ...form, id: Number(id) });
            else await createEnterpriseCustomForm(form);
            notifyOperationSuccess("已保存");
            void navigate("/lianYunUser/enterpriseCustom");
        } catch {
            setErrorMessage("企业定制保存失败");
        } finally {
            setSaving(false);
        }
    };

    // 复用旧 Vue 表单字段的 label、必填标识和输入转换，布局迁移不改变校验与提交行为。
    const renderField = (
        field: { key: keyof EnterpriseCustomFormData; label: string; required?: boolean },
        span = field.key === "customerId"
            ? 2
            : field.key === "copyright"
              ? 3
              : field.key === "icpFilings" || field.key === "internetFilings"
                ? 2
                : 1
    ) => (
        <ZhGridItem key={field.key} span={span}>
            <label className="flex flex-col gap-1">
                <span>
                    {field.required ? <RequiredMark /> : null}
                    {field.label}
                </span>
                {field.key === "customerId" ? (
                    <>
                        <Select
                            allowClear
                            aria-label={field.label}
                            disabled={editing}
                            filterOption={false}
                            loading={searchingCustomer}
                            options={customerOptions.map(({ label, value }) => ({ label, value }))}
                            placeholder="请输入关键字选择链云用户"
                            showSearch
                            value={form.customerId || undefined}
                            onSearch={keyword => void searchCustomers(keyword)}
                            onChange={value => {
                                const selected = customerOptions.find(option => option.value === value);
                                setForm(current => ({ ...current, customerId: value, customJson: selected?.snapshot }));
                                setErrorMessage("");
                            }}
                        />
                        <Input
                            aria-label="链云用户"
                            className="sr-only"
                            disabled={editing}
                            value={String(form.customerId ?? "")}
                            onChange={event => updateField("customerId", event.target.value)}
                        />
                    </>
                ) : field.key === "userId" ? (
                    <>
                        <Select
                            allowClear
                            aria-label={field.label}
                            disabled={editing}
                            filterOption={false}
                            loading={searchingUser}
                            options={userOptions.map(({ label, value }) => ({ label, value }))}
                            placeholder="请输入关键字选择关联企业定制用户"
                            showSearch
                            value={form.userId || undefined}
                            onSearch={keyword => void searchUsers(keyword)}
                            onChange={value => {
                                const selected = userOptions.find(option => option.value === value);
                                setForm(current => ({ ...current, userId: value, userJson: selected?.snapshot }));
                                setErrorMessage("");
                            }}
                        />
                        <Input
                            aria-label="企业定制用户"
                            className="sr-only"
                            disabled={editing}
                            value={String(form.userId ?? "")}
                            onChange={event => updateField("userId", event.target.value)}
                        />
                    </>
                ) : (
                    <Input
                        id={String(field.key)}
                        aria-label={field.label}
                        maxLength={fieldMaxLength[field.key]}
                        placeholder={`请输入${field.label}`}
                        showCount={Boolean(fieldMaxLength[field.key])}
                        value={String(form[field.key] ?? "")}
                        onChange={event => updateField(field.key, event.target.value)}
                    />
                )}
            </label>
        </ZhGridItem>
    );

    const renderFieldByKey = (key: keyof EnterpriseCustomFormData, span?: number) => {
        const field = fieldMap.get(key);
        return field ? renderField(field, span) : null;
    };

    return (
        <section
            aria-label="enterprise-custom-form-page"
            className="detail-page-transparent flex h-full min-h-full flex-col"
        >
            <Card>
                <Typography.Title className="mb-5 mt-0" level={4}>
                    {editing ? "编辑企业定制" : "新增企业定制"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" role="alert" title={errorMessage} type="error" /> : null}
                <div className="section-title mb-5">账号信息</div>
                <ZhGrid data-area="account" gap={[30, 2]} cols={4}>
                    {renderFieldByKey("customerId")}
                </ZhGrid>
                {customerInfo?.code ? (
                    <>
                        <div className="section-title mb-5 mt-4">客户信息</div>
                        <ZhGrid data-area="customer" className="customer-info" gap={[30, 0]} cols={4}>
                            <ZhGridItem span={2}>
                                <div className="flex flex-col gap-1">
                                    <span>账号</span>
                                    <span>
                                        {displayValue(customerInfo.institutionName || customerInfo.realName)} |{" "}
                                        {formatMobile(customerInfo.mobile)} | {displayValue(customerInfo.code)}
                                    </span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>身份</span>
                                    <span>{getCustomerIdentityList(customerInfo as Customer)}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>个人实名</span>
                                    <span>{displayValue(customerInfo.realName)}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>企业实名</span>
                                    <span>{displayValue(customerInfo.institutionName)}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>会员等级</span>
                                    <span>{displayValue(customerInfo.memberLevelText)}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>会员生效日期</span>
                                    <span>
                                        {customerInfo.memberActiveTime
                                            ? dateFormat(customerInfo.memberActiveTime)
                                            : "--"}
                                    </span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <div className="flex flex-col gap-1">
                                    <span>会员失效日期</span>
                                    <span>
                                        {customerInfo.memberExpiryDate
                                            ? dateFormat(customerInfo.memberExpiryDate)
                                            : "--"}
                                    </span>
                                </div>
                            </ZhGridItem>
                        </ZhGrid>
                    </>
                ) : null}
                <div className="section-title mb-5 mt-4">关联企业用户</div>
                <ZhGrid data-area="relation" className="mt-4" gap={[30, 2]} cols={4}>
                    {renderFieldByKey("userId")}
                </ZhGrid>
                <div className="section-title mb-5 mt-4">基本信息</div>
                <ZhGrid data-area="basic" gap={[30, 2]} cols={4}>
                    {basicFieldKeys.map(key => renderFieldByKey(key))}
                </ZhGrid>
                <div className="section-title mb-5 mt-4">配置信息</div>
                <ZhGrid data-area="config" gap={[30, 2]} cols={4}>
                    {renderFieldByKey(configFieldKeys[0], 3)}
                    {renderFieldByKey(configFieldKeys[1], 3)}
                    <ZhGridItem span={1} />
                    {renderFieldByKey(configFieldKeys[2], 1)}
                    {renderFieldByKey(configFieldKeys[3], 1)}
                    {renderFieldByKey(configFieldKeys[4], 2)}
                </ZhGrid>
                <Space className="operation-fixed-btn" size={0}>
                    <Button autoInsertSpace={false} loading={saving} type="primary" onClick={() => void submit()}>
                        保存
                    </Button>
                    <Button
                        autoInsertSpace={false}
                        type="primary"
                        onClick={() => void navigate("/lianYunUser/enterpriseCustom")}
                    >
                        返回
                    </Button>
                </Space>
            </Card>
        </section>
    );
}

export default EnterpriseCustomFormPage;
